import React, { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Badge from "../../components/common/Badge";
import { useGetBusinessQuery, useGetLicensesQuery } from "../../store/api";
import { formatDateTime } from "../../utils/reporting";
import { computeIsExpired, getCurrentLicense } from "../../utils/license";

const formatDateOnly = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
};

const getErrorCode = (problem) => {
  const code =
    problem?.data?.code || problem?.data?.errorCode || problem?.data?.title;
  return typeof code === "string" ? code.trim().toUpperCase() : "";
};

const isLicenseExpiredProblem = (problem) =>
  getErrorCode(problem) === "LICENSE_EXPIRED";

const LicenseHistory = () => {
  const { businessId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: business } = useGetBusinessQuery(businessId);
  const { data, isLoading, isFetching, isError, error } =
    useGetLicensesQuery(businessId);
  const licenseState = useSelector((state) => state.license);

  const currentLicense = useMemo(() => getCurrentLicense(data), [data]);
  const isExpired =
    computeIsExpired(currentLicense) || licenseState?.isExpired === true;
  const isExpiredError = isLicenseExpiredProblem(error);
  const statusLabel = isExpired ? "Expired" : "Active";
  const statusTone = isExpired ? "danger" : "success";
  const planName =
    currentLicense?.planName || currentLicense?.planCode || "Not available";
  const showSuccessBanner = Boolean(location.state?.licenseUpdated);

  useEffect(() => {
    if (!showSuccessBanner) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, navigate, showSuccessBanner]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="License Overview"
        subtitle={`Current license status for ${business?.businessName || "this business"}.`}
        actions={
          <Link to={`/businesses/${businessId}/licenses/edit`}>
            <Button size="sm" disabled={isLoading || isFetching}>
              Edit License
            </Button>
          </Link>
        }
      />

      {showSuccessBanner && (
        <Alert tone="success">License updated successfully.</Alert>
      )}

      {isExpired && (
        <Alert tone="error">License expired. Please renew to continue.</Alert>
      )}

      {isError && !isExpiredError && (
        <Alert tone="error">
          {error?.data?.detail || "Failed to load current license."}
        </Alert>
      )}

      <Card>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Business
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {business?.businessName || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Subscription Plan
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">{planName}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              License Status
            </p>
            <div className="mt-1">
              <Badge tone={statusTone}>{statusLabel}</Badge>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Last Updated
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {formatDateTime(currentLicense?.updatedAt)}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Start Date
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {formatDateOnly(currentLicense?.startsOn)}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              End Date
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {formatDateOnly(currentLicense?.endsOn)}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Grace Days
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {currentLicense?.graceDays ?? "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Subscription ID
            </p>
            <p className="mt-1 break-all text-sm font-medium text-slate-900">
              {currentLicense?.subscriptionId || "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Max Stores
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {currentLicense?.maxStores ?? "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Max Tills / Store
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {currentLicense?.maxTillsPerStore ?? "-"}
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Max Kiosks / Store
            </p>
            <p className="mt-1 text-sm font-medium text-slate-900">
              {currentLicense?.maxKiosksPerStore ?? "-"}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LicenseHistory;
